import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useOutletContext } from "react-router-dom";
import {
  getBudgetSettingsError,
  saveBudgetSetting,
  updateError,
} from "store/budgetSettingsSlice";
import { api } from 'helpers/axios';
import * as Sentry from "@sentry/react";
import { errorMessage } from "helpers/common";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import { Controller, useForm } from "react-hook-form";
import { toast } from "helpers/toast";
import { helpTextIcon } from "helpers/helpText";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { getLang } from "store/userSlice";
import { Field, Input, CurrencyField, Select, NativeSelect, RichText, Button, SectionHeading, FormGroupBox } from "components/ui";

export default function BudgetForm({ id, handleChangeSelectedBudget, fetchAllBudgets, batchUpdateCheckedList = [], handleEnableBatchUpdate = null }) {
  const dispatch = useDispatch();

  const { helps } = useOutletContext();

  const [error, setError] = useState(null);

  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const [calculationOptions, setCalculationOptions] = useState([]);
  const [calculationModels, setCalculationModels] = useState(null);
  const [showDays, setShowDays] = useState(false);
  const [showMonths, setShowMonths] = useState(false);

  const [disableBudgetAmount, setDisableBudgetAmount] = useState(false);

  const [mixError, setMixError] = useState(false);
  const [budgetGroupDetails, setBudgetGroupDetails] = useState(null);

  const isBatchUpdate = useMemo(() => batchUpdateCheckedList.length > 0, [batchUpdateCheckedList]);

  const [batchUpdateFields, setBatchUpdateFields] = useState([]);

  const [reportLogos, setReportLogos] = useState([]);

  const [descriptionError, setDescriptionError] = useState(false);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const schema = yup.object({
    budgetGroup: yup.string().notRequired(),
    budgetName: yup.string().required().max(255).label(translate('budget_setting.budget_name')),
    calculationModel: yup.object().required().label(translate('budget_setting.calculation_model')),
    days: yup.number().integer().min(0).max(366).notRequired().label(translate('budget_setting.days')),
    months: yup.number().integer().min(0).max(12).notRequired().label(translate('budget_setting.budget_months')),
    budgetAmount: // if input, must be positive number. if empty, not required
      yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .test('is-decimal', translate('form.valid_format_decimal_2'), value => (value?.toString())?.match(/^\d*(,{1}(\d{1,2}))?$/))
        .label(translate('budget_setting.budget_amount')),
    reportLogo: yup.string().notRequired().label(translate('report_logo.title')),
    description: yup.string().notRequired().label(translate('budget_setting.description')),
    lockVersion: yup.string().notRequired()
  });

  const { register, control, setValue, getValues, reset, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchCalculationModel = watch('calculationModel');
  const watchBudgetGroup = watch('budgetGroup');
  const watchDescription = watch('description');

  const displayAmountField = useMemo(() => {
    if (isBatchUpdate) {
      if (batchUpdateCheckedList[0].startsWith('budget-')) return true;
    } else if (!watchBudgetGroup) return true;

    return false;
  }, [watchBudgetGroup, isBatchUpdate, batchUpdateCheckedList]);

  useEffect(() => {
    if (watchBudgetGroup) {
      fetchBudgetGroup();
    } else {
      setBudgetGroupDetails(null);
    }
  }, [watchBudgetGroup]);

  useEffect(() => {
    fetchCalculations();
    fetchReportLogos();
    setDescriptionError(false);
  }, [id]);

  useEffect(() => {
    if (!watchCalculationModel || !calculationModels) {
      return;
    }

    const selectedModel = calculationModels.find(
      m => m.hash_id === watchCalculationModel.value
    );

    if (!selectedModel) {
      setShowDays(false);
      setShowMonths(false);
      setDisableBudgetAmount(false);
      return;
    }

    const selectedModelIsWithoutBudget = !!selectedModel?.is_without_budget;

    const shouldShowDays =
      selectedModel?.full_settings?.should_use_absence_in_calculation?.length > 0;

    const shouldShowMonths =
      shouldShowDays ||
      ['always', 'more_than'].includes(
        selectedModel?.full_settings?.should_rest_period_reduce_performance_pay
      );

    setShowDays(shouldShowDays);
    setShowMonths(shouldShowMonths);

    setMixError(false);
    setError(null);

    if (selectedModelIsWithoutBudget) {
      setDisableBudgetAmount(true);
      setValue('budgetAmount', '');
    } else {
      setDisableBudgetAmount(false);
    }

    if (id && watchBudgetGroup && budgetGroupDetails) {
      let allWithoutBudget = true;
      for (const b of budgetGroupDetails?.budget_settings) {
        if (b?.hash_id !== id && ((selectedModelIsWithoutBudget && !b?.calculation_model?.is_without_budget) || (!selectedModelIsWithoutBudget && b?.calculation_model?.is_without_budget))) {
          allWithoutBudget = false;
          break;
        }
      }
      if (!allWithoutBudget) {
        setMixError(true);
        setError({ translationKey: 'calculation_settings.mix_error' });
      }
    }
  }, [watchCalculationModel]);

  const fetchBudgetGroup = async () => {
    setError(null);
    try {
      const { data, status } = await api.get(`/settings/budget-groups/${watchBudgetGroup}`);
      if (status === 200) {
        setBudgetGroupDetails(data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch budget group'));
      Sentry.captureException(err);
      setBudgetGroupDetails(null);
    }
  };

  const fetchCalculations = async () => {
    try {
      const { data, status } = await api.get(`/settings/calculation`);
      if (status === 200) {
        const options = [];
        data.data.forEach(d => {
          options.push({
            value: d.hash_id,
            label: d.name
          });
        });
        setCalculationOptions(options);
        setCalculationModels(data.data);

        if (id) {
          fetchBudgetSetting(options);
        } else {
          reset({
            budgetGroup: null,
            budgetName: null,
            calculationModel: null,
            days: null,
            months: null,
            budgetAmount: null,
            reportLogo: null,
            description: null,
            lockVersion: null
          });
          setShowMonths(false);
          setShowDays(false);
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch calculation settings'));
      Sentry.captureException(err);
    }
  };

  const handleSubmission = async (submission) => {
    if (descriptionError) return;

    try {
      const data = {
        ...submission,
        calculationModel: submission.calculationModel.value
      };
      if (!showDays) {
        delete data.days;
      }
      if (!showMonths) {
        delete data.months;
      }
      if (id) {
        data.hashId = id;
      }

      await dispatch(saveBudgetSetting(data)).unwrap();
      fetchAllBudgets();
      toast.success(translate('form.success.saved'));
      handleChangeSelectedBudget(null);
    } catch (err) {
      dispatch(updateError(null));
      setError(errorMessage(err, 'save budget'));
    }
  };

  const handleBatchUpdateSubmit = async (e) => {
    e.preventDefault();

    const allValues = getValues();
    const values = {};
    batchUpdateFields.forEach(f => {
      values[f] = allValues[f];

      if (f === 'calculationModel') values[f] = allValues[f].value;
    });

    const formData = {
      hash_ids: batchUpdateCheckedList.map(l => l.replace('budget-', '').replace('child-', '')),
      ...values
    };

    try {
      const { status } = await api.post(`/settings/budgets/batch-update`, formData);

      if (status === 200) {
        fetchAllBudgets();
        toast.success(translate('form.success.saved'));
        handleEnableBatchUpdate();
      }
    } catch (err) {
      setError(errorMessage(err, 'save budget batch update'));
      Sentry.captureException(err);
    }
  };

  const fetchBudgetSetting = async (options) => {
    try {
      const { data } = await api.get(`/settings/budgets/${id}`);
      const fullSettings = data.full_settings;
      const selectedCalculation = options.filter(o => o.value === fullSettings.calculationModel);

      reset({
        budgetGroup: data.budget_group?.hash_id,
        budgetName: fullSettings.budgetName,
        calculationModel: { ...selectedCalculation[0] },
        days: fullSettings.days,
        months: fullSettings.months,
        budgetAmount: fullSettings.budgetAmount,
        reportLogo: data.report_logo?.hash_id,
        description: fullSettings.description,
        lockVersion: data.lock_version
      });
    } catch (err) {
      setError(errorMessage(err, 'fetch budget'));
      Sentry.captureException(err);
    }
  };

  const handleDescriptionChange = (val) => {
    setValue('description', val);
  };

  const getLabel = (key) => {
    const sKey = key.replace('.', '-');
    const help = helps.filter(h => h.key === sKey)[0] || null;

    return (
      <>
        {editableTranslate(key)}
        {help && helpTextIcon(sKey, help.short_text)}
      </>
    )
  };

  const BatchCheck = ({ field, disabled = false }) => (
    <input
      type="checkbox"
      data-testid={`batch-update-field-${field}`}
      className="mr-1 align-middle"
      checked={batchUpdateFields.includes(field)}
      disabled={disabled}
      onChange={(e) => handleCheckBatchUpdateFields(e, field)}
    />
  );

  const handleCheckBatchUpdateFields = (e, field) => {
    if (e.target.checked) {
      const newBatchUpdateFields = [...batchUpdateFields];
      newBatchUpdateFields.push(field);
      setBatchUpdateFields(newBatchUpdateFields);
    } else {
      setBatchUpdateFields(batchUpdateFields.filter(f => f !== field));
    }
  };

  const fetchReportLogos = async () => {
    try {
      const { data, status } = await api.get('/settings/report-logos');
      if (status === 200) {
        setReportLogos(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch report logos'));
      Sentry.captureException(err);
    }
  };

  return (
    <div className={`bg-white w-100 p-4 content-card`}>
      <div>
        <SectionHeading
          title={<>
            {isBatchUpdate && <span className="mr-1">({editableTranslate('budget_setting.batch_update')})</span>}
            {editableTranslate('budget_setting.individual_budget')}
          </>}
          desc={editableTranslate('budget_setting.individual_budget_description')}
        />

        {error && <div>
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </div>}

        <form className="pt-3" onSubmit={isBatchUpdate ? handleBatchUpdateSubmit : handleSubmit(handleSubmission)}>
          <FormGroupBox className="mb-4">
            <div className="peb-field-row peb-field-mb">
              {
                !isBatchUpdate &&
                <Field label={getLabel('budget_setting.budget_name')} required htmlFor="budgetName" error={errors?.budgetName?.message}>
                  <Input id="budgetName" maxLength={255} error={!!errors?.budgetName} {...register('budgetName')} />
                </Field>
              }
              <Field
                label={getLabel('budget_setting.calculation_model')}
                required={!isBatchUpdate}
                prefix={isBatchUpdate && <BatchCheck field="calculationModel" />}
                error={errors?.calculationModel?.message}
              >
                <Controller
                  name="calculationModel"
                  control={control}
                  render={({ field }) => <Select {...field} options={calculationOptions} isDisabled={isBatchUpdate && !batchUpdateFields.includes('calculationModel')} placeholder={translate('form.select_placeholder')} error={!!errors?.calculationModel} />}
                />
              </Field>
            </div>
            <div className="peb-field-row peb-field-mb">
              {
                displayAmountField &&
                <Field
                  label={<>{getLabel('budget_setting.budget_amount')} {disableBudgetAmount && <i className="bi bi-info-circle ml-1" data-tooltip-id={`without_budget_tooltip`}></i>}</>}
                  prefix={isBatchUpdate && <BatchCheck field="budgetAmount" disabled={disableBudgetAmount} />}
                  error={errors?.budgetAmount?.message}
                >
                  <Controller
                    control={control}
                    name="budgetAmount"
                    render={({ field: { onChange, value } }) => (
                      <CurrencyField data-testid="budget-amount-input"
                        value={value}
                        allowNegativeValue={false}
                        maxLength={16}
                        disabled={(isBatchUpdate && !batchUpdateFields.includes('budgetAmount')) || disableBudgetAmount}
                        onValueChange={(value, name, values) => onChange(value || null)}
                        error={!!errors?.budgetAmount}
                      />
                    )}
                  />
                  {
                    disableBudgetAmount && <ReactTooltip id={`without_budget_tooltip`} place="bottom">{translate('tooltips.calculation_settings.without_budget')}</ReactTooltip>
                  }
                </Field>
              }
              {
                showMonths &&
                <Field label={getLabel('budget_setting.budget_months')} prefix={isBatchUpdate && <BatchCheck field="months" />} error={errors?.months?.message}>
                  <Input type="number" number error={!!errors?.months} {...register('months')} disabled={isBatchUpdate && !batchUpdateFields.includes('months')} min={0} max={12} />
                </Field>
              }
              {
                showDays &&
                <Field label={getLabel('budget_setting.days')} prefix={isBatchUpdate && <BatchCheck field="days" />} error={errors?.days?.message}>
                  <Input type="number" number error={!!errors?.days} {...register('days')} disabled={isBatchUpdate && !batchUpdateFields.includes('days')} min={0} max={366} />
                </Field>
              }
              {
                (displayAmountField && reportLogos.length > 1) &&
                <Field label={getLabel('report_logo.title')} prefix={isBatchUpdate && <BatchCheck field="reportLogo" />}>
                  <NativeSelect {...register('reportLogo')} disabled={isBatchUpdate && !batchUpdateFields.includes('reportLogo')}>
                    <option></option>
                    {
                      reportLogos.map((reportLogo, i) => <option key={i} value={reportLogo.hash_id}>{reportLogo.name}</option>)
                    }
                  </NativeSelect>
                </Field>
              }
              {
                <input type="hidden" {...register('lockVersion')} data-testid="lock-version-input" />
              }
            </div>
            <Field label={getLabel('budget_setting.description')} prefix={isBatchUpdate && <BatchCheck field="description" />}>
              <RichText data-testid="description-input"
                className={isBatchUpdate && !batchUpdateFields.includes('description') ? 'hide-toolbar' : ''}
                value={watchDescription}
                onChangeCallback={(v) => handleDescriptionChange(v)}
                validate={(v) => v ? setDescriptionError(true) : setDescriptionError(false)}
                readOnly={isBatchUpdate && !batchUpdateFields.includes('description')}
                error={descriptionError}
              />
            </Field>
          </FormGroupBox>
          <div className="text-right">
            <Button data-testid="cancel-button" variant="secondary" className="mr-2" onClick={() => isBatchUpdate ? handleEnableBatchUpdate() : handleChangeSelectedBudget(null)}>{editableTranslate('button.cancel')}</Button>
            <Button data-testid="save-button" variant="primary" type="submit" disabled={(isBatchUpdate && batchUpdateFields.length < 1) || mixError}>{editableTranslate('button.save')}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
